import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import { useStickyState } from "@/hooks/useStickyState";

/**
 * Shared, persisted store of deals added while in Lovable preview mode.
 *
 * The Deals page keeps its own transient UI state (drag-drop stage overrides,
 * cell edits, etc.), but a preview-added deal has to survive a route change
 * because the "+ Add Deal" button lives in AppLayout — the AddDealDialog can
 * be opened from *any* page, not just /deals. Without a shared store, the
 * DealsPage mounts fresh after navigation and never learns about the new deal.
 *
 * Persisted via localStorage so it also survives full refreshes in preview.
 */
export interface PreviewAddedDeal {
  id: string;
  company: string;
  name: string;
  stage: string;
  amount: number;
  closeDate: string;
  contacts: string;
  owner: string;
  lastEmail: string;
  lastMeeting: string;
  nextMeeting: string;
  billing: string;
  lastStep: string;
  nextStep: string;
  createdDate: string;
  createdBy: string;
  stageEnteredDate: string;
  // Allow forecastCategory / custom keys without narrowing every consumer.
  [extra: string]: unknown;
}

interface Ctx {
  addedDeals: PreviewAddedDeal[];
  addDeal: (deal: PreviewAddedDeal) => void;
  removeDeal: (id: string) => void;
  clear: () => void;
}

const PreviewAddedDealsContext = createContext<Ctx | null>(null);

const STORAGE_KEY = "preview-added-deals-v1";

export function PreviewAddedDealsProvider({ children }: { children: ReactNode }) {
  const [addedDeals, setAddedDeals] = useStickyState<PreviewAddedDeal[]>(STORAGE_KEY, []);

  const addDeal = useCallback((deal: PreviewAddedDeal) => {
    setAddedDeals(prev => [...prev, deal]);
    // Notify already-mounted consumers on the same tab (useStickyState only
    // reads localStorage at mount, and the "storage" event is same-origin
    // cross-tab only).
    window.dispatchEvent(new CustomEvent("preview-deals-changed"));
  }, [setAddedDeals]);

  const removeDeal = useCallback((id: string) => {
    setAddedDeals(prev => prev.filter(d => d.id !== id));
    window.dispatchEvent(new CustomEvent("preview-deals-changed"));
  }, [setAddedDeals]);

  const clear = useCallback(() => {
    setAddedDeals([]);
    window.dispatchEvent(new CustomEvent("preview-deals-changed"));
  }, [setAddedDeals]);

  const value = useMemo(() => ({ addedDeals, addDeal, removeDeal, clear }), [addedDeals, addDeal, removeDeal, clear]);

  return (
    <PreviewAddedDealsContext.Provider value={value}>
      {children}
    </PreviewAddedDealsContext.Provider>
  );
}

export function usePreviewAddedDeals(): Ctx {
  const ctx = useContext(PreviewAddedDealsContext);
  if (!ctx) throw new Error("usePreviewAddedDeals must be used within PreviewAddedDealsProvider");
  return ctx;
}